import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Skeleton,
} from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { Braces, LayoutGrid, Plus, Trash2 } from 'lucide-react';
import {
  categoryIconFor,
  taxonomyCategoriesQueryOptions,
  UNCATEGORIZED_CATEGORY,
  useBulkDeleteTaxonomyCategories,
  useDeleteTaxonomyCategory,
  useSeedStandardCategories,
  type TaxonomyCategory,
} from '@/entities/taxonomy-category';
import { usePermissions } from '@/entities/user';
import { PageTransition } from '@/shared/ui/animations';
import {
  TaxonomyEmptyState,
  TaxonomyErrorState,
  TaxonomyNoResultsState,
} from '../components/TaxonomyStates/TaxonomyStates';
import { MonksFlowMarkIcon } from '@/shared/ui/Icons';
import { FormDialog } from '@/shared/ui/FormDialog';
import { SearchInput } from '@/shared/ui/SearchInput';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';
import {
  ROW_WITHOUT_FOCUS_RING,
  TableIconBadge,
  redesignTableStyles,
} from '@/shared/ui/data-table';
import { formatShortDate } from '@/shared/utils/format';
import { AddStandardCategoriesModal } from './components/AddStandardCategoriesModal/AddStandardCategoriesModal';
import { CategoryActionMenu } from './components/CategoryActionMenu/CategoryActionMenu';
import { CategoryModal } from './components/CategoryModal/CategoryModal';

/** Placeholder rows while the registry loads, matching the other tabs. */
const SKELETON_ROWS = Array.from({ length: 5 }, (_, i) => i);
/** Icon, Name, Fields, Scope, Last modified — plus Select and Actions for a writer. */
const SKELETON_CELLS = Array.from({ length: 5 }, (_, i) => i);
const SKELETON_CELLS_WRITABLE = Array.from({ length: 7 }, (_, i) => i);

/**
 * Categories tab — the single place field groupings are defined.
 *
 * Fields choose from these; they can no longer invent one while being edited.
 * That's the point of the tab: a category carries an icon and a rail position,
 * and letting those be created inline produced near-duplicate groups nobody
 * owned.
 */
export function TaxonomyCategories() {
  const { t } = useTranslation('knowledge');
  // Registry writes are manage-guarded server-side; mirror that here so the tab
  // reads as a catalog rather than an editor nobody is allowed to use.
  const { canManageTaxonomy } = usePermissions();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<TaxonomyCategory | null>(null);
  const [creating, setCreating] = useState(false);
  const [standardOpen, setStandardOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  // Owned by the page: deleting drops the row, and a Radix dialog unmounted
  // while open leaves the page unclickable.
  const [deleting, setDeleting] = useState<TaxonomyCategory | null>(null);
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const query = useQuery(taxonomyCategoriesQueryOptions());
  const remove = useDeleteTaxonomyCategory();
  const seedStandard = useSeedStandardCategories();

  const categories = useMemo<TaxonomyCategory[]>(() => query.data ?? [], [query.data]);

  /** Names the registry already holds, so the picker can't offer a duplicate. */
  const existingNames = useMemo(() => new Set(categories.map((c) => c.name)), [categories]);

  /**
   * Where the next hand-made category goes: after everything real, before the
   * holding pen.
   *
   * `Uncategorized` sits at 999 deliberately, so a naive `max + 1` would put new
   * categories at 1000 — below the bucket that only exists to catch orphans.
   * Excluding it keeps the sentinel last, which is the whole point of the gap.
   */
  const nextSortOrder = useMemo(() => {
    const placed = categories
      .map((c) => c.sortOrder)
      .filter((n) => n < UNCATEGORIZED_CATEGORY.sortOrder);
    return placed.length === 0 ? 0 : Math.max(...placed) + 1;
  }, [categories]);

  const filtered = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    if (!term) return categories;
    return categories.filter((c) => c.name.toLowerCase().includes(term));
  }, [categories, debouncedSearch]);

  // Selection tracks only what's currently VISIBLE, so a filtered-out row can
  // never be deleted by a "select all" the user couldn't see.
  const visibleSelected = useMemo(
    () => filtered.filter((c) => selectedIds.has(c.id)),
    [filtered, selectedIds],
  );
  const allVisibleSelected = filtered.length > 0 && visibleSelected.length === filtered.length;

  /** Fields the pending bulk delete would re-file, for the confirm copy. */
  const selectedFieldCount = useMemo(
    () => visibleSelected.reduce((sum, c) => sum + c.fieldCount, 0),
    [visibleSelected],
  );

  // Functional updater, not `new Set(selectedIds)`: two ticks in the same batch
  // would both read the same snapshot and the second would drop the first.
  const toggleRow = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllVisible = () =>
    setSelectedIds(allVisibleSelected ? new Set() : new Set(filtered.map((c) => c.id)));

  const bulkDelete = useBulkDeleteTaxonomyCategories({
    onSuccess: () => {
      setBulkDeleteOpen(false);
      setSelectedIds(new Set());
    },
  });

  const isEmpty = !query.isLoading && !query.isError && categories.length === 0;
  /**
   * Filtered to nothing is NOT the same as empty, and gating only on the
   * unfiltered total meant a search matching no rows rendered a bare column
   * header over an empty body — with no `role="status"` either, so assistive
   * technology was told nothing at all.
   */
  const showNoResults =
    !query.isLoading && !query.isError && categories.length > 0 && filtered.length === 0;

  return (
    <>
      <PageTransition variant="fade" className="flex min-h-0 flex-1 flex-col">
        {!isEmpty && (
          <div className="flex items-center justify-between gap-3 px-section py-3">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('taxonomy.taxonomyCategories.searchCategories')}
              aria-label={t('taxonomy.taxonomyCategories.searchCategories2')}
              className="w-72"
            />
            <div className="flex items-center gap-2">
              {canManageTaxonomy && visibleSelected.length > 0 && (
                <Button
                  variant="destructive"
                  size="mini"
                  className="h-8"
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  <Trash2 className="size-3.5" />
                  {t('taxonomy.taxonomyCategories.deleteCategories', {
                    count: visibleSelected.length,
                  })}
                </Button>
              )}
              {canManageTaxonomy && (
                <>
                  <Button
                    variant="outline"
                    size="mini"
                    className="h-8"
                    onClick={() => setStandardOpen(true)}
                    title={t('taxonomy.taxonomyCategories.addMonksflowStandardCategoriesRegistry')}
                  >
                    <MonksFlowMarkIcon className="h-3.5 w-[1.3rem]" />
                    {t('taxonomy.taxonomyCategories.addStandardCategories')}
                  </Button>
                  <Button size="mini" className="h-8" onClick={() => setCreating(true)}>
                    <Plus className="size-3.5" />
                    {t('taxonomy.taxonomyCategories.newCategory')}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {query.isError ? (
          <TaxonomyErrorState
            title={t('taxonomy.taxonomyStates.couldnTLoadCategories')}
            onRetry={() => void query.refetch()}
          />
        ) : isEmpty ? (
          <TaxonomyEmptyState
            icon={<LayoutGrid className="size-6 text-muted-foreground" aria-hidden />}
            title={t('taxonomy.taxonomyCategories.noCategoriesYet')}
            description={t('taxonomy.taxonomyCategories.groupsYourFieldDefinitionsEach')}
          >
            {canManageTaxonomy && (
              <div className="flex items-center gap-2">
                <Button size="mini" onClick={() => setStandardOpen(true)}>
                  <MonksFlowMarkIcon className="h-3.5 w-[1.3rem]" />
                  {t('taxonomy.taxonomyCategories.addStandardCategories')}
                </Button>
                <Button variant="outline" size="mini" onClick={() => setCreating(true)}>
                  <Plus className="size-3.5" />
                  {t('taxonomy.taxonomyCategories.createYourOwn')}
                </Button>
              </div>
            )}
          </TaxonomyEmptyState>
        ) : showNoResults ? (
          <TaxonomyNoResultsState
            title={t('taxonomy.taxonomyStates.noCategoriesFound')}
            description={t('taxonomy.taxonomyStates.noCategoriesMatchYourSearch')}
            onClear={() => setSearch('')}
          />
        ) : (
          <div className="min-h-0 flex-1 overflow-auto px-section pb-section">
            {/* Same Datatable primitives and shared list-table styles as Lists,
                Fields and Schemas, so the tabs read identically. */}
            <Datatable
              aria-busy={query.isLoading || undefined}
              className={redesignTableStyles.table}
            >
              <DatatableHeader>
                <DatatableRow className={ROW_WITHOUT_FOCUS_RING}>
                  {canManageTaxonomy && (
                    <DatatableHead scope="col" className="w-10">
                      <Checkbox
                        checked={allVisibleSelected}
                        onCheckedChange={toggleAllVisible}
                        aria-label={
                          allVisibleSelected
                            ? t('taxonomy.taxonomyCategories.clearSelection')
                            : t('taxonomy.taxonomyCategories.selectAllCategories')
                        }
                      />
                    </DatatableHead>
                  )}
                  <DatatableHead scope="col" className="w-18">
                    <span className="sr-only">{t('taxonomy.taxonomyCategories.icon')}</span>
                  </DatatableHead>
                  <DatatableHead scope="col">{t('taxonomy.taxonomyCategories.name')}</DatatableHead>
                  <DatatableHead scope="col" className="w-28">
                    {t('taxonomy.taxonomyCategories.fields')}
                  </DatatableHead>
                  <DatatableHead scope="col" className="w-32">
                    {t('taxonomy.taxonomyCategories.scope')}
                  </DatatableHead>
                  <DatatableHead scope="col" className="w-36">
                    {t('taxonomy.taxonomyCategories.lastModified')}
                  </DatatableHead>
                  {canManageTaxonomy && (
                    <DatatableHead scope="col" className="w-13">
                      <span className="sr-only">{t('taxonomy.taxonomyCategories.actions')}</span>
                    </DatatableHead>
                  )}
                </DatatableRow>
              </DatatableHeader>
              <DatatableBody>
                {query.isLoading
                  ? SKELETON_ROWS.map((r) => (
                      <DatatableRow key={r} className="h-[72px]">
                        {(canManageTaxonomy ? SKELETON_CELLS_WRITABLE : SKELETON_CELLS).map((c) => (
                          <DatatableCell key={c}>
                            {c === (canManageTaxonomy ? 1 : 0) ? (
                              <Skeleton className="size-10 rounded-lg" />
                            ) : (
                              <Skeleton className="h-4 w-full" />
                            )}
                          </DatatableCell>
                        ))}
                      </DatatableRow>
                    ))
                  : filtered.map((c) => {
                      const Icon = categoryIconFor(c.name, c.icon);
                      return (
                        <DatatableRow
                          key={c.id}
                          interactive={canManageTaxonomy}
                          className={`h-[72px] ${ROW_WITHOUT_FOCUS_RING}`}
                          onClick={canManageTaxonomy ? () => setEditing(c) : undefined}
                        >
                          {canManageTaxonomy && (
                            <DatatableCell onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedIds.has(c.id)}
                                onCheckedChange={() => toggleRow(c.id)}
                                aria-label={t('taxonomy.taxonomyCategories.selectCategory', {
                                  name: c.name,
                                })}
                              />
                            </DatatableCell>
                          )}
                          <DatatableCell>
                            <TableIconBadge>
                              <Icon className="size-5 text-foreground" aria-hidden />
                            </TableIconBadge>
                          </DatatableCell>
                          <DatatableCell>
                            <span className="text-sm font-medium text-foreground">{c.name}</span>
                          </DatatableCell>
                          <DatatableCell>
                            <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground">
                              <Braces className="size-4 text-muted-foreground" aria-hidden />
                              {c.fieldCount}
                            </span>
                          </DatatableCell>
                          <DatatableCell>
                            <span className="text-sm text-foreground">
                              {c.orgWide
                                ? t('taxonomy.taxonomyCategories.organization')
                                : t('taxonomy.taxonomyCategories.workspace')}
                            </span>
                          </DatatableCell>
                          <DatatableCell>
                            <span className="text-sm text-foreground">
                              {formatShortDate(c.updatedAt)}
                            </span>
                          </DatatableCell>
                          {canManageTaxonomy && (
                            <DatatableCell className="text-right">
                              <CategoryActionMenu
                                category={c}
                                onEdit={() => setEditing(c)}
                                onDelete={() => setDeleting(c)}
                              />
                            </DatatableCell>
                          )}
                        </DatatableRow>
                      );
                    })}
              </DatatableBody>
            </Datatable>
          </div>
        )}
      </PageTransition>

      {bulkDeleteOpen && (
        <FormDialog
          open
          onOpenChange={(next) => !next && setBulkDeleteOpen(false)}
          title={t('taxonomy.taxonomyCategories.deleteCategoriesTitle', {
            count: visibleSelected.length,
          })}
          className="flex max-h-[calc(100dvh-64px)] flex-col sm:!max-w-[28rem]"
        >
          <FormDialog.Body className="flex min-h-0 flex-col gap-3 overflow-y-auto">
            <p className="text-sm text-muted-foreground">
              {selectedFieldCount > 0 ? (
                <>
                  {t('taxonomy.taxonomyCategories.fieldsWillMoveTo', {
                    count: selectedFieldCount,
                    target: UNCATEGORIZED_CATEGORY.name,
                  })}
                </>
              ) : (
                <>{t('taxonomy.taxonomyCategories.nothingUsesTheseCategoriesSo')}</>
              )}
            </p>
            <ul className="flex flex-col gap-0.5">
              {visibleSelected.map((c) => (
                <li key={c.id} className="truncate text-xs text-muted-foreground">
                  {c.name}
                  <span className="ml-1 opacity-70">
                    ({t('taxonomy.taxonomyCategories.fieldCount', { count: c.fieldCount })})
                  </span>
                </li>
              ))}
            </ul>
          </FormDialog.Body>
          <FormDialog.Footer
            primary={{
              label: t('taxonomy.taxonomyCategories.deleteCategories', {
                count: visibleSelected.length,
              }),
              onClick: () => bulkDelete.mutate(visibleSelected.map((c) => c.id)),
              disabled: bulkDelete.isPending,
              loading: bulkDelete.isPending,
            }}
            secondary={{
              label: t('taxonomy.taxonomyCategories.cancel'),
              onClick: () => setBulkDeleteOpen(false),
            }}
          />
        </FormDialog>
      )}

      {standardOpen && (
        <AddStandardCategoriesModal
          open
          addedNames={existingNames}
          isSubmitting={seedStandard.isPending}
          onClose={() => setStandardOpen(false)}
          onAdd={(names) => seedStandard.mutate(names, { onSuccess: () => setStandardOpen(false) })}
        />
      )}

      {(creating || editing) && (
        <CategoryModal
          open
          category={editing}
          nextSortOrder={nextSortOrder}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      {deleting && (
        <FormDialog
          open
          onOpenChange={(next) => !next && setDeleting(null)}
          title={t('taxonomy.taxonomyCategories.deleteCategoryTitle', { name: deleting.name })}
          className="flex flex-col sm:!max-w-[28rem]"
        >
          <FormDialog.Body>
            {/* No in-use guard on purpose: no field data is destroyed, so
                refusing would be a guard against nothing. But the fields do
                MOVE, and a delete that quietly re-files someone's work has to
                say so before it happens, not after. */}
            <p className="text-sm text-muted-foreground">
              {deleting.fieldCount > 0 ? (
                <>
                  {t('taxonomy.taxonomyCategories.fieldsInWillMoveTo', {
                    count: deleting.fieldCount,
                    from: deleting.name,
                    target: UNCATEGORIZED_CATEGORY.name,
                  })}
                </>
              ) : (
                <>{t('taxonomy.taxonomyCategories.nothingUsesCategorySoNothing')}</>
              )}
            </p>
          </FormDialog.Body>
          <FormDialog.Footer
            primary={{
              label: t('taxonomy.taxonomyCategories.deleteCategory'),
              onClick: () => remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) }),
              disabled: remove.isPending,
              loading: remove.isPending,
            }}
            secondary={{
              label: t('taxonomy.taxonomyCategories.cancel'),
              onClick: () => setDeleting(null),
            }}
          />
        </FormDialog>
      )}
    </>
  );
}

TaxonomyCategories.displayName = 'TaxonomyCategories';
